import { useState, useRef, useCallback, useEffect } from 'react';
import { MODE } from '../constants/gameConstants';
import { PAINT_MODE, PAINT_CATEGORY } from '../data/paintObjects';
import { syncActiveMapState } from '../utils/gameStateManager';
import {
  createObjectInstance,
  canPlaceObject,
  placeObjectOnGrid,
  removeObjectFromGrid,
  isObjectType,
  getObjectAtCell,
  getObjectDefinition,
  getRotatedDimensions,
  rotateObjectInstance,
} from '../utils/objectUtils';

const plotLine = (x0, y0, x1, y1) => {
  const points = [];
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  while (true) {
    points.push({ i: x0, j: y0 });
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }
  return points;
};

/**
 * Grid painting (terrain, fog, walls, painted objects) + placed object selection, drag, context menu.
 * @param {object} params.grid - colors, walls, objects, instances, width/height + setters
 * @param {object} params.ui - selectedPaintColor, isPaintMode, paintMode, mode, categoryMode
 * @param {object} params.selection - selectedObjectId + setter
 * @param {object} params.sync - roomId, viewingMapId, currentMapId, socketHook
 */
export function useGridSession({ grid, ui, selection, sync }) {
  const {
    gridColors,
    setGridColors,
    gridWalls,
    setGridWalls,
    gridObjects,
    setGridObjects,
    gridObjectInstances,
    setGridObjectInstances,
    gridWidth,
    gridHeight,
  } = grid;
  const { selectedPaintColor, isPaintMode, paintMode, mode, categoryMode } = ui;
  const { selectedObjectId, setSelectedObjectId } = selection;
  const { roomId, viewingMapId, currentMapId, socketHook } = sync;

  const [isPaintDragging, setIsPaintDragging] = useState(false);
  const [lastPaintedPosition, setLastPaintedPosition] = useState(null);
  const [areaPaintStart, setAreaPaintStart] = useState(null);
  const [areaPaintEnd, setAreaPaintEnd] = useState(null);
  const [wallHoverCorner, setWallHoverCorner] = useState(null);
  const [wallDragStartCorner, setWallDragStartCorner] = useState(null);
  const [wallDragEndCorner, setWallDragEndCorner] = useState(null);

  const pendingUpdatesRef = useRef({});
  const pendingWallUpdatesRef = useRef({});
  const pendingObjectUpdatesRef = useRef({});
  const pendingInstanceUpdatesRef = useRef({});
  const debouncedUpdateRef = useRef(null);

  const gridObjectsRef = useRef(gridObjects);
  const gridObjectInstancesRef = useRef(gridObjectInstances);

  gridObjectsRef.current = gridObjects;
  gridObjectInstancesRef.current = gridObjectInstances;

  const getWallEdgeKey = useCallback((from, to) => {
    const di = to.i - from.i;
    const dj = to.j - from.j;
    if (di === 1 && dj === 0) return `v:${from.i + 1}-${from.j}`;
    if (di === -1 && dj === 0) return `v:${from.i}-${from.j}`;
    if (di === 0 && dj === 1) return `h:${from.i}-${from.j + 1}`;
    if (di === 0 && dj === -1) return `h:${from.i}-${from.j}`;
    return null;
  }, []);

  const collectWallKeysForPath = useCallback(
    (points) => {
      const keys = new Set();
      if (!points || points.length < 2) return keys;

      for (let idx = 1; idx < points.length; idx++) {
        const prev = points[idx - 1];
        const curr = points[idx];
        const di = curr.i - prev.i;
        const dj = curr.j - prev.j;

        if (Math.abs(di) === 1 && Math.abs(dj) === 1) {
          const stepA = { i: prev.i + di, j: prev.j };
          const keyA = getWallEdgeKey(prev, stepA);
          if (keyA) keys.add(keyA);
          const keyB = getWallEdgeKey(stepA, curr);
          if (keyB) keys.add(keyB);
        } else {
          const key = getWallEdgeKey(prev, curr);
          if (key) keys.add(key);
        }
      }

      return keys;
    },
    [getWallEdgeKey]
  );

  const collectWallKeysFromCorners = useCallback((startCorner, endCorner) => {
    if (!startCorner || !endCorner) return new Set();

    const points = plotLine(startCorner.x, startCorner.y, endCorner.x, endCorner.y);
    const keys = new Set();

    for (let idx = 1; idx < points.length; idx++) {
      const prev = points[idx - 1];
      const curr = points[idx];
      const dx = curr.i - prev.i;
      const dy = curr.j - prev.j;

      if (Math.abs(dx) === 1 && Math.abs(dy) === 1) {
        const mid = { x: prev.i + dx, y: prev.j };
        keys.add(`h:${Math.min(prev.i, mid.x)}-${prev.j}`);
        keys.add(`v:${mid.x}-${Math.min(mid.y, curr.j)}`);
      } else if (dx !== 0) {
        keys.add(`h:${Math.min(prev.i, curr.i)}-${prev.j}`);
      } else if (dy !== 0) {
        keys.add(`v:${prev.i}-${Math.min(prev.j, curr.j)}`);
      }
    }

    return keys;
  }, []);

  const handleBatchGridColorUpdate = useCallback(
    (points) => {
      const isObjectColor = isObjectType(selectedPaintColor);
      const structureType =
        typeof selectedPaintColor === 'object' ? selectedPaintColor?.structure : null;
      const isStructurePaint = structureType === 'wall' || structureType === 'door';
      const isClearAction = typeof selectedPaintColor === 'object' && selectedPaintColor.clearColor;

      console.log('[Paint] handleBatchGridColorUpdate called:', {
        selectedPaintColor,
        isObjectColor,
        isClearAction,
        points: points.length,
      });

      if (isClearAction) {
        const eraseType = selectedPaintColor.eraseType;
        console.log('[Paint] Erase action - eraseType:', eraseType);

        if (eraseType === 'terrain') {
          setGridColors((prev) => {
            const updated = { ...prev };
            points.forEach(({ i, j }) => {
              const cellKey = `${i}-${j}`;
              const currentCell = prev[cellKey];
              let newColor;

              if (typeof currentCell === 'object' && currentCell.fog) {
                newColor = { fog: true };
              } else {
                newColor = undefined;
              }

              pendingUpdatesRef.current[cellKey] = newColor;

              if (newColor === undefined) {
                delete updated[cellKey];
              } else {
                updated[cellKey] = newColor;
              }
            });
            return updated;
          });
        } else if (eraseType === 'objects') {
          const currentGridObjects = gridObjectsRef.current;
          const currentInstances = gridObjectInstancesRef.current;

          const instancesToRemove = new Set();
          const instancesToDelete = {};

          points.forEach(({ i, j }) => {
            const instance = getObjectAtCell(i, j, currentGridObjects, currentInstances);
            console.log(`[Object Erase] Checking cell ${i}-${j}:`, instance);
            if (instance) {
              instancesToRemove.add(instance.id);
              instancesToDelete[instance.id] = instance;
            }
          });

          console.log('[Object Erase] Found instances to remove:', instancesToRemove.size);

          if (instancesToRemove.size > 0) {
            setGridObjectInstances((prev) => {
              const updated = { ...prev };
              instancesToRemove.forEach((id) => {
                delete updated[id];
                pendingInstanceUpdatesRef.current[id] = null;
              });
              return updated;
            });

            setGridObjects((prev) => {
              let updated = { ...prev };
              Object.values(instancesToDelete).forEach((instance) => {
                updated = removeObjectFromGrid(instance, updated);
              });
              return updated;
            });
          }
        } else if (eraseType === 'walls') {
          setGridWalls((prev) => {
            const updated = { ...prev };
            const wallKeys = collectWallKeysForPath(points);

            wallKeys.forEach((key) => {
              delete updated[key];
              pendingWallUpdatesRef.current[key] = undefined;
            });
            return updated;
          });
        }
      } else if (isStructurePaint) {
        setGridWalls((prev) => {
          const updated = { ...prev };
          const wallKeys = collectWallKeysForPath(points);
          wallKeys.forEach((key) => {
            updated[key] = structureType;
            pendingWallUpdatesRef.current[key] = structureType;
          });
          return updated;
        });
      } else if (isObjectColor) {
        const objectDef = getObjectDefinition(selectedPaintColor);
        if (!objectDef) return;

        const newInstances = {};
        const updatedGridObjects = { ...gridObjects };

        points.forEach(({ i, j }) => {
          const { width, height } = getRotatedDimensions(objectDef, 0);

          if (
            canPlaceObject(i, j, width, height, gridWidth, gridHeight, updatedGridObjects)
          ) {
            const instance = createObjectInstance(selectedPaintColor, i, j, 0);
            if (instance) {
              newInstances[instance.id] = instance;
              const updatedGrid = placeObjectOnGrid(instance, updatedGridObjects);
              Object.assign(updatedGridObjects, updatedGrid);
            }
          }
        });

        if (Object.keys(newInstances).length > 0) {
          setGridObjectInstances((prev) => {
            const updated = { ...prev, ...newInstances };
            Object.assign(pendingInstanceUpdatesRef.current, newInstances);
            return updated;
          });
          setGridObjects(updatedGridObjects);
        }
      } else {
        setGridColors((prev) => {
          const updated = { ...prev };

          points.forEach(({ i, j }) => {
            const cellKey = `${i}-${j}`;
            const currentCell = prev[cellKey];
            let newColor;

            if (!selectedPaintColor) {
              newColor = undefined;
            } else if (typeof selectedPaintColor === 'object') {
              if (selectedPaintColor.clearFog) {
                if (typeof currentCell === 'object' && currentCell.fog) {
                  newColor = currentCell.color || undefined;
                } else {
                  newColor = currentCell;
                }
              } else if (selectedPaintColor.fog) {
                if (typeof currentCell === 'object') {
                  newColor = { ...currentCell, fog: true };
                } else if (currentCell) {
                  newColor = { color: currentCell, fog: true };
                } else {
                  newColor = { fog: true };
                }
              }
            } else {
              if (typeof currentCell === 'object' && currentCell.fog) {
                newColor = { color: selectedPaintColor, fog: true };
              } else {
                newColor = selectedPaintColor;
              }
            }

            pendingUpdatesRef.current[cellKey] = newColor;

            if (newColor === undefined) {
              delete updated[cellKey];
            } else {
              updated[cellKey] = newColor;
            }
          });

          return updated;
        });
      }

      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }

      debouncedUpdateRef.current = setTimeout(() => {
        const colorUpdates = { ...pendingUpdatesRef.current };
        const newColors = { ...gridColors };

        Object.entries(colorUpdates).forEach(([key, color]) => {
          if (color === undefined) {
            delete newColors[key];
          } else {
            newColors[key] = color;
          }
        });

        const wallUpdates = { ...pendingWallUpdatesRef.current };
        const newWalls = { ...gridWalls };
        Object.entries(wallUpdates).forEach(([key, wallVal]) => {
          if (wallVal === undefined) {
            delete newWalls[key];
          } else {
            newWalls[key] = wallVal;
          }
        });

        const objectUpdates = { ...pendingObjectUpdatesRef.current };
        const newObjects = { ...gridObjects };

        Object.entries(objectUpdates).forEach(([key, obj]) => {
          if (obj === undefined) {
            delete newObjects[key];
          } else {
            newObjects[key] = obj;
          }
        });

        const instanceUpdates = { ...pendingInstanceUpdatesRef.current };
        const newInstances = { ...gridObjectInstances };

        Object.entries(instanceUpdates).forEach(([id, instance]) => {
          if (instance === null) {
            delete newInstances[id];
          } else {
            newInstances[id] = instance;
          }
        });

        syncActiveMapState({
          roomId,
          viewingMapId,
          currentMapId,
          socketHook,
          patch: {
            gridColors: newColors,
            gridWalls: newWalls,
            gridObjects: newObjects,
            gridObjectInstances: newInstances,
          },
        });

        pendingUpdatesRef.current = {};
        pendingWallUpdatesRef.current = {};
        pendingObjectUpdatesRef.current = {};
        pendingInstanceUpdatesRef.current = {};
        debouncedUpdateRef.current = null;
      }, 500);
    },
    [
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      gridWidth,
      gridHeight,
      selectedPaintColor,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      setGridColors,
      setGridWalls,
      setGridObjects,
      setGridObjectInstances,
      collectWallKeysForPath,
    ]
  );

  const handleGridMouseDown = useCallback(
    (i, j, meta, onCloseContextMenu) => {
      if (!isPaintMode) return;

      if (onCloseContextMenu) {
        onCloseContextMenu();
        return;
      }

      setIsPaintDragging(true);

      const structureType =
        typeof selectedPaintColor === 'object' ? selectedPaintColor?.structure : null;
      const isStructurePaint = structureType === 'wall' || structureType === 'door';
      const isWallErase =
        typeof selectedPaintColor === 'object' &&
        selectedPaintColor?.clearColor &&
        selectedPaintColor?.eraseType === 'walls';
      const isWallCornerMode = isStructurePaint || isWallErase;
      if (isWallCornerMode && meta?.corner) {
        setWallDragStartCorner(meta.corner);
        setWallDragEndCorner(meta.corner);
        return;
      }

      if (paintMode === PAINT_MODE.LINE) {
        setLastPaintedPosition({ i, j });
        handleBatchGridColorUpdate([{ i, j }]);
      } else if (paintMode === PAINT_MODE.AREA) {
        setAreaPaintStart({ i, j });
        setAreaPaintEnd({ i, j });
      }
    },
    [isPaintMode, paintMode, handleBatchGridColorUpdate, selectedPaintColor]
  );

  const handleGridMouseEnter = useCallback(
    (i, j, meta) => {
      if (!isPaintMode) return;

      const structureType =
        typeof selectedPaintColor === 'object' ? selectedPaintColor?.structure : null;
      const isWallPaint = structureType === 'wall' || structureType === 'door';
      const isWallErase =
        typeof selectedPaintColor === 'object' &&
        selectedPaintColor?.clearColor &&
        selectedPaintColor?.eraseType === 'walls';
      const isWallCornerMode = isWallPaint || isWallErase;
      if (isWallCornerMode) {
        if (meta?.corner) {
          setWallHoverCorner(meta.corner);
          if (isPaintDragging && wallDragStartCorner) {
            setWallDragEndCorner(meta.corner);
          }
        }
        return;
      }

      if (!isPaintDragging) return;

      if (paintMode === PAINT_MODE.LINE) {
        if (lastPaintedPosition && lastPaintedPosition.i === i && lastPaintedPosition.j === j) {
          return;
        }

        if (lastPaintedPosition) {
          const points = plotLine(lastPaintedPosition.i, lastPaintedPosition.j, i, j);
          handleBatchGridColorUpdate(points);
        } else {
          handleBatchGridColorUpdate([{ i, j }]);
        }

        setLastPaintedPosition({ i, j });
      } else if (paintMode === PAINT_MODE.AREA) {
        setAreaPaintEnd({ i, j });
      }
    },
    [
      isPaintMode,
      isPaintDragging,
      paintMode,
      lastPaintedPosition,
      handleBatchGridColorUpdate,
      selectedPaintColor,
      wallDragStartCorner,
    ]
  );

  const handleGridMouseUp = useCallback(
    (meta) => {
      const structureType =
        typeof selectedPaintColor === 'object' ? selectedPaintColor?.structure : null;
      const isWallPaint = structureType === 'wall' || structureType === 'door';
      const isWallErase =
        typeof selectedPaintColor === 'object' &&
        selectedPaintColor?.clearColor &&
        selectedPaintColor?.eraseType === 'walls';
      if (isWallPaint || isWallErase) {
        const endCorner = meta?.corner || wallDragEndCorner || wallHoverCorner;
        if (wallDragStartCorner && endCorner) {
          const wallKeys = collectWallKeysFromCorners(wallDragStartCorner, endCorner);
          if (wallKeys.size > 0) {
            setGridWalls((prev) => {
              const updated = { ...prev };
              wallKeys.forEach((key) => {
                if (isWallErase) {
                  delete updated[key];
                  pendingWallUpdatesRef.current[key] = undefined;
                } else {
                  updated[key] = structureType;
                  pendingWallUpdatesRef.current[key] = structureType;
                }
              });
              return updated;
            });

            if (debouncedUpdateRef.current) {
              clearTimeout(debouncedUpdateRef.current);
            }
            debouncedUpdateRef.current = setTimeout(() => {
              const wallUpdates = { ...pendingWallUpdatesRef.current };
              const newWalls = { ...gridWalls };
              Object.entries(wallUpdates).forEach(([key, wallVal]) => {
                if (wallVal === undefined) {
                  delete newWalls[key];
                } else {
                  newWalls[key] = wallVal;
                }
              });
              syncActiveMapState({
                roomId,
                viewingMapId,
                currentMapId,
                socketHook,
                patch: { gridWalls: newWalls },
              });
              pendingWallUpdatesRef.current = {};
              debouncedUpdateRef.current = null;
            }, 500);
          }
        }

        setIsPaintDragging(false);
        setLastPaintedPosition(null);
        setAreaPaintStart(null);
        setAreaPaintEnd(null);
        setWallDragStartCorner(null);
        setWallDragEndCorner(null);
        return;
      }

      if (isPaintMode && paintMode === PAINT_MODE.AREA && areaPaintStart && areaPaintEnd) {
        const minI = Math.min(areaPaintStart.i, areaPaintEnd.i);
        const maxI = Math.max(areaPaintStart.i, areaPaintEnd.i);
        const minJ = Math.min(areaPaintStart.j, areaPaintEnd.j);
        const maxJ = Math.max(areaPaintStart.j, areaPaintEnd.j);

        const points = [];
        for (let i = minI; i <= maxI; i++) {
          for (let j = minJ; j <= maxJ; j++) {
            points.push({ i, j });
          }
        }

        handleBatchGridColorUpdate(points);
      }

      setIsPaintDragging(false);
      setLastPaintedPosition(null);
      setAreaPaintStart(null);
      setAreaPaintEnd(null);
    },
    [
      isPaintMode,
      paintMode,
      areaPaintStart,
      areaPaintEnd,
      handleBatchGridColorUpdate,
      selectedPaintColor,
      wallDragStartCorner,
      wallDragEndCorner,
      wallHoverCorner,
      collectWallKeysFromCorners,
      setGridWalls,
      gridWalls,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
    ]
  );

  const handleGridMouseLeave = useCallback(() => {
    setIsPaintDragging(false);
    setLastPaintedPosition(null);
    setAreaPaintStart(null);
    setAreaPaintEnd(null);
    setWallHoverCorner(null);
    setWallDragStartCorner(null);
    setWallDragEndCorner(null);
  }, []);

  const handleClearAllColors = useCallback(() => {
    setGridColors({});

    syncActiveMapState({
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      patch: { gridColors: {} },
    });
  }, [roomId, viewingMapId, currentMapId, socketHook, setGridColors]);

  const [contextMenuObject, setContextMenuObject] = useState(null);
  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuObject(null);
    setContextMenuPosition(null);
  }, []);

  const handleObjectRightClick = useCallback(
    (instance, screenX, screenY) => {
      if (mode === MODE.PAINT && categoryMode === PAINT_CATEGORY.OBJECTS) {
        setContextMenuObject(instance);
        setContextMenuPosition({ x: screenX, y: screenY });
      }
    },
    [mode, categoryMode]
  );

  const handleContextRotateObject = useCallback(() => {
    if (!contextMenuObject) return;

    const result = rotateObjectInstance(
      contextMenuObject,
      gridWidth,
      gridHeight,
      gridObjects,
      gridObjectInstances
    );

    if (result) {
      setGridObjectInstances((prev) => ({
        ...prev,
        [contextMenuObject.id]: result.instance,
      }));
      setGridObjects(result.gridObjects);
      setContextMenuObject(result.instance);

      syncActiveMapState({
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        patch: {
          gridObjects: result.gridObjects,
          gridObjectInstances: {
            ...gridObjectInstances,
            [contextMenuObject.id]: result.instance,
          },
        },
      });
    }
  }, [
    contextMenuObject,
    gridWidth,
    gridHeight,
    gridObjects,
    gridObjectInstances,
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
    setGridObjectInstances,
    setGridObjects,
  ]);

  const handleContextDeleteObject = useCallback(() => {
    if (!contextMenuObject) return;

    const updatedGridObjects = removeObjectFromGrid(contextMenuObject, gridObjects);
    setGridObjects(updatedGridObjects);

    setGridObjectInstances((prev) => {
      const updated = { ...prev };
      delete updated[contextMenuObject.id];
      return updated;
    });

    handleCloseContextMenu();

    const newInstances = { ...gridObjectInstances };
    delete newInstances[contextMenuObject.id];
    syncActiveMapState({
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      patch: {
        gridObjects: updatedGridObjects,
        gridObjectInstances: newInstances,
      },
    });
  }, [
    contextMenuObject,
    gridObjects,
    gridObjectInstances,
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
    handleCloseContextMenu,
    setGridObjects,
    setGridObjectInstances,
  ]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenuObject) {
        handleCloseContextMenu();
      }
    };

    if (contextMenuObject) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenuObject, handleCloseContextMenu]);

  useEffect(() => {
    if (mode !== MODE.PAINT || categoryMode !== PAINT_CATEGORY.OBJECTS) {
      handleCloseContextMenu();
    }
  }, [mode, categoryMode, handleCloseContextMenu]);

  const handleObjectDragEnd = useCallback(
    (instance, newAnchorX, newAnchorY) => {
      if (!instance) return;

      console.log(
        '[App] handleObjectDragEnd called:',
        instance.id,
        'from',
        instance.anchorX,
        instance.anchorY,
        'to',
        newAnchorX,
        newAnchorY
      );

      if (contextMenuObject && contextMenuObject.id === instance.id) {
        handleCloseContextMenu();
      }

      if (instance.anchorX === newAnchorX && instance.anchorY === newAnchorY) {
        console.log('[App] Position unchanged, skipping update');
        return;
      }

      const clearedGrid = removeObjectFromGrid(instance, gridObjects);

      const displayWidth =
        instance.rotation === 90 || instance.rotation === 270 ? instance.height : instance.width;
      const displayHeight =
        instance.rotation === 90 || instance.rotation === 270 ? instance.width : instance.height;

      if (
        !canPlaceObject(
          newAnchorX,
          newAnchorY,
          displayWidth,
          displayHeight,
          gridWidth,
          gridHeight,
          clearedGrid
        )
      ) {
        console.log('Cannot move object - position invalid or occupied');
        return;
      }

      const updatedInstance = {
        ...instance,
        anchorX: newAnchorX,
        anchorY: newAnchorY,
      };

      const updatedGrid = placeObjectOnGrid(updatedInstance, clearedGrid);

      setGridObjectInstances((prev) => ({
        ...prev,
        [instance.id]: updatedInstance,
      }));

      setGridObjects(updatedGrid);

      pendingInstanceUpdatesRef.current[instance.id] = updatedInstance;

      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }

      debouncedUpdateRef.current = setTimeout(() => {
        const instanceUpdates = { ...pendingInstanceUpdatesRef.current };
        const newInstances = { ...gridObjectInstances };

        Object.entries(instanceUpdates).forEach(([id, inst]) => {
          if (inst === null) {
            delete newInstances[id];
          } else {
            newInstances[id] = inst;
          }
        });

        syncActiveMapState({
          roomId,
          viewingMapId,
          currentMapId,
          socketHook,
          patch: {
            gridObjects: updatedGrid,
            gridObjectInstances: newInstances,
          },
        });

        pendingInstanceUpdatesRef.current = {};
        debouncedUpdateRef.current = null;
      }, 500);
    },
    [
      gridObjects,
      gridObjectInstances,
      gridWidth,
      gridHeight,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      contextMenuObject,
      handleCloseContextMenu,
      setGridObjectInstances,
      setGridObjects,
    ]
  );

  const handleRotateObject = useCallback(() => {
    if (!selectedObjectId || !gridObjectInstances[selectedObjectId]) {
      return;
    }

    const instance = gridObjectInstances[selectedObjectId];
    const result = rotateObjectInstance(
      instance,
      gridWidth,
      gridHeight,
      gridObjects,
      gridObjectInstances
    );

    if (!result) {
      console.log('Cannot rotate object - not enough space');
      return;
    }

    setGridObjectInstances((prev) => ({
      ...prev,
      [selectedObjectId]: result.instance,
    }));

    setGridObjects(result.gridObjects);

    pendingInstanceUpdatesRef.current[selectedObjectId] = result.instance;

    if (debouncedUpdateRef.current) {
      clearTimeout(debouncedUpdateRef.current);
    }

    debouncedUpdateRef.current = setTimeout(() => {
      const instanceUpdates = { ...pendingInstanceUpdatesRef.current };
      const newInstances = { ...gridObjectInstances };

      Object.entries(instanceUpdates).forEach(([id, inst]) => {
        if (inst === null) {
          delete newInstances[id];
        } else {
          newInstances[id] = inst;
        }
      });

      syncActiveMapState({
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        patch: {
          gridObjects: result.gridObjects,
          gridObjectInstances: newInstances,
        },
      });

      pendingInstanceUpdatesRef.current = {};
      debouncedUpdateRef.current = null;
    }, 500);
  }, [
    selectedObjectId,
    gridObjectInstances,
    gridWidth,
    gridHeight,
    gridObjects,
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
    setGridObjectInstances,
    setGridObjects,
  ]);

  const handleDeleteObject = useCallback(() => {
    if (!selectedObjectId || !gridObjectInstances[selectedObjectId]) {
      return;
    }

    const instance = gridObjectInstances[selectedObjectId];

    const updatedGridObjects = removeObjectFromGrid(instance, gridObjects);
    setGridObjects(updatedGridObjects);

    setGridObjectInstances((prev) => {
      const updated = { ...prev };
      delete updated[selectedObjectId];
      return updated;
    });

    pendingInstanceUpdatesRef.current[selectedObjectId] = null;

    setSelectedObjectId(null);

    const newInstances = { ...gridObjectInstances };
    delete newInstances[selectedObjectId];
    syncActiveMapState({
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      patch: {
        gridObjects: updatedGridObjects,
        gridObjectInstances: newInstances,
      },
    });
  }, [
    selectedObjectId,
    gridObjectInstances,
    gridObjects,
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
    setSelectedObjectId,
    setGridObjects,
    setGridObjectInstances,
  ]);

  return {
    isPaintDragging,
    areaPaintStart,
    areaPaintEnd,
    wallHoverCorner,
    wallDragStartCorner,
    wallDragEndCorner,
    handleBatchGridColorUpdate,
    handleGridMouseDown,
    handleGridMouseEnter,
    handleGridMouseUp,
    handleGridMouseLeave,
    handleClearAllColors,
    contextMenuObject,
    contextMenuPosition,
    handleCloseContextMenu,
    handleObjectRightClick,
    handleContextRotateObject,
    handleContextDeleteObject,
    handleObjectDragEnd,
    handleRotateObject,
    handleDeleteObject,
  };
}
